import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Shield, Users, MousePointerClick,
  Trash2, UserX, UserCheck, TrendingUp, Calendar, Search,
  BarChart3, Home, LogOut, Crown, Link2, ExternalLink,
  ToggleLeft, ToggleRight, ShieldCheck, ShieldOff,
  Pencil, X, Save, Monitor, Smartphone, Globe, BarChart2,
  Eye, EyeOff, Plus, Copy, Bot, User, Layers
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = window.location.origin + '/api';
const DOMAIN = window.location.host;

const SOURCE_COLORS = ['#06B6D4', '#8B5CF6', '#F59E0B', '#10B981', '#EF4444', '#EC4899', '#F97316', '#6366F1'];

function StatBar({ label, value, total, color = '#06B6D4' }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="text-slate-400 text-sm w-28 truncate shrink-0">{label}</span>
      <div className="flex-1 bg-white/5 rounded-full h-2">
        <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-white text-sm font-medium w-16 text-right shrink-0">{value} <span className="text-slate-500 text-xs">({pct}%)</span></span>
    </div>
  );
}

const AdminPanel = () => {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [allLinks, setAllLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const [editingLink, setEditingLink] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', original_url: '', short_code: '' });
  const [editSaving, setEditSaving] = useState(false);

  const [analyticsLink, setAnalyticsLink] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [cloakedLinks, setCloakedLinks] = useState([]);
  const [cloakerLoading, setCloakerLoading] = useState(false);
  const [cloakerSearch, setCloakerSearch] = useState('');
  const [showCloakerCreate, setShowCloakerCreate] = useState(false);
  const [editingCloaker, setEditingCloaker] = useState(null);
  const [cloakerForm, setCloakerForm] = useState({ short_code: '', title: '', real_url: '', safe_url: '' });
  const [cloakerSaving, setCloakerSaving] = useState(false);
  const [cloakerStats, setCloakerStats] = useState(null);
  const [cloakerStatsLink, setCloakerStatsLink] = useState(null);
  const [cloakerStatsLoading, setCloakerStatsLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(response.data);
    } catch (error) { console.error('Failed to fetch stats:', error); }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) { setUsers([]); }
  }, [token]);

  const fetchAllLinks = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/links`, { headers: { Authorization: `Bearer ${token}` } });
      setAllLinks(Array.isArray(response.data) ? response.data : []);
    } catch (error) { setAllLinks([]); }
  }, [token]);

  const fetchCloakedLinks = useCallback(async () => {
    setCloakerLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/cloaker`, { headers: { Authorization: `Bearer ${token}` } });
      setCloakedLinks(Array.isArray(response.data) ? response.data : []);
    } catch (error) { setCloakedLinks([]); }
    finally { setCloakerLoading(false); }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchUsers(), fetchAllLinks()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStats, fetchUsers, fetchAllLinks]);

  useEffect(() => {
    if (activeTab === 'cloaker') fetchCloakedLinks();
  }, [activeTab, fetchCloakedLinks]);

  const handleToggleUserStatus = async (userId) => {
    try {
      await axios.put(`${API_URL}/admin/users/${userId}/toggle-status`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Kullanıcı durumu güncellendi');
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'İşlem başarısız'); }
  };

  const handleTogglePremium = async (userId) => {
    try {
      const response = await axios.put(`${API_URL}/admin/users/${userId}/toggle-premium`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.is_premium ? 'Premium aktif edildi' : 'Premium kaldırıldı');
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'İşlem başarısız'); }
  };

  const handleToggleAdmin = async (userId) => {
    if (!window.confirm('Bu kullanıcının admin yetkisini değiştirmek istediğinize emin misiniz?')) return;
    try {
      const response = await axios.put(`${API_URL}/admin/users/${userId}/toggle-admin`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.is_admin ? 'Admin yetkisi verildi' : 'Admin yetkisi kaldırıldı');
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'İşlem başarısız'); }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`${username} kullanıcısını ve tüm verilerini silmek istediğinize emin misiniz?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Kullanıcı silindi');
      fetchUsers(); fetchStats();
    } catch (error) { toast.error(error.response?.data?.detail || 'Silme başarısız'); }
  };

  const handleToggleLinkStatus = async (linkId) => {
    try {
      const response = await axios.put(`${API_URL}/admin/links/${linkId}/toggle-status`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.is_active ? 'Link aktif edildi' : 'Link devre dışı bırakıldı');
      fetchAllLinks();
    } catch (error) { toast.error('İşlem başarısız'); }
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Bu linki silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/admin/links/${linkId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Link silindi');
      fetchAllLinks(); fetchStats();
    } catch (error) { toast.error('Silme başarısız'); }
  };

  const handleOpenEdit = (link) => {
    setEditingLink(link);
    setEditForm({ title: link.title || '', original_url: link.original_url || '', short_code: link.short_code || '' });
  };

  const handleSaveEdit = async () => {
    if (!editingLink) return;
    setEditSaving(true);
    try {
      await axios.put(`${API_URL}/admin/links/${editingLink.id}`, {
        title: editForm.title || null,
        original_url: editForm.original_url,
        short_code: editForm.short_code !== editingLink.short_code ? editForm.short_code : null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Link güncellendi');
      setEditingLink(null);
      fetchAllLinks();
    } catch (error) { toast.error(error.response?.data?.detail || 'Güncelleme başarısız'); }
    finally { setEditSaving(false); }
  };

  const handleViewAnalytics = async (link) => {
    setAnalyticsLink(link);
    setAnalyticsData(null);
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/links/${link.id}/analytics`, { headers: { Authorization: `Bearer ${token}` } });
      setAnalyticsData(res.data);
    } catch (error) { toast.error('Analitik verisi alınamadı'); setAnalyticsLink(null); }
    finally { setAnalyticsLoading(false); }
  };

  const openCloakerCreate = () => {
    setEditingCloaker(null);
    setCloakerForm({ short_code: '', title: '', real_url: '', safe_url: '' });
    setShowCloakerCreate(true);
  };

  const openCloakerEdit = (link) => {
    setEditingCloaker(link);
    setCloakerForm({ short_code: link.short_code, title: link.title || '', real_url: link.real_url, safe_url: link.safe_url });
    setShowCloakerCreate(true);
  };

  const handleSaveCloaker = async () => {
    if (!cloakerForm.short_code || !cloakerForm.real_url || !cloakerForm.safe_url) {
      toast.error('Kısa kod, gerçek URL ve güvenli URL zorunludur');
      return;
    }
    setCloakerSaving(true);
    try {
      if (editingCloaker) {
        await axios.put(`${API_URL}/admin/cloaker/${editingCloaker.id}`, {
          title: cloakerForm.title || null,
          real_url: cloakerForm.real_url,
          safe_url: cloakerForm.safe_url,
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Cloaker linki güncellendi');
      } else {
        await axios.post(`${API_URL}/admin/cloaker`, {
          short_code: cloakerForm.short_code,
          title: cloakerForm.title || null,
          real_url: cloakerForm.real_url,
          safe_url: cloakerForm.safe_url,
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Cloaker linki oluşturuldu');
      }
      setShowCloakerCreate(false);
      fetchCloakedLinks();
    } catch (error) { toast.error(error.response?.data?.detail || 'İşlem başarısız'); }
    finally { setCloakerSaving(false); }
  };

  const handleToggleCloaker = async (linkId, isActive) => {
    try {
      await axios.put(`${API_URL}/admin/cloaker/${linkId}`, { is_active: !isActive }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(!isActive ? 'Aktif edildi' : 'Devre dışı bırakıldı');
      fetchCloakedLinks();
    } catch (error) { toast.error('İşlem başarısız'); }
  };

  const handleDeleteCloaker = async (linkId) => {
    if (!window.confirm('Bu cloaker linkini silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/admin/cloaker/${linkId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Link silindi');
      fetchCloakedLinks();
    } catch (error) { toast.error('Silme başarısız'); }
  };

  const handleViewCloakerStats = async (link) => {
    setCloakerStatsLink(link);
    setCloakerStats(null);
    setCloakerStatsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/cloaker/${link.id}/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setCloakerStats(res.data);
    } catch (error) { toast.error('İstatistik alınamadı'); setCloakerStatsLink(null); }
    finally { setCloakerStatsLoading(false); }
  };

  const copyLink = (shortCode) => {
    navigator.clipboard.writeText(`${window.location.origin}/c/${shortCode}`);
    toast.success('Link kopyalandı');
  };

  const filteredUsers = (Array.isArray(users) ? users : []).filter(u =>
    u?.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u?.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredLinks = (Array.isArray(allLinks) ? allLinks : []).filter(l =>
    l?.short_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l?.original_url?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l?.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredCloaked = cloakedLinks.filter(l =>
    l?.short_code?.toLowerCase().includes(cloakerSearch.toLowerCase()) ||
    l?.title?.toLowerCase().includes(cloakerSearch.toLowerCase()) ||
    l?.real_url?.toLowerCase().includes(cloakerSearch.toLowerCase())
  );

  const handleLogout = () => { logout(); navigate('/'); };

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center"><div className="spinner" /></div>;
  }

  const deviceTotal = analyticsData ? Object.values(analyticsData.devices || {}).reduce((a, b) => a + b, 0) : 0;
  const browserTotal = analyticsData ? Object.values(analyticsData.browsers || {}).reduce((a, b) => a + b, 0) : 0;
  const countryTotal = analyticsData ? Object.values(analyticsData.countries || {}).reduce((a, b) => a + b, 0) : 0;
  const topCountries = analyticsData ? Object.entries(analyticsData.countries || {}).sort((a, b) => b[1] - a[1]).slice(0, 8) : [];
  const topBrowsers = analyticsData ? Object.entries(analyticsData.browsers || {}).sort((a, b) => b[1] - a[1]).slice(0, 6) : [];
  const recentDailyClicks = analyticsData ? (analyticsData.daily_clicks || []).slice(-14) : [];

  const cloakerDailyData = cloakerStats ? (cloakerStats.daily || []).slice(-14) : [];
  const cloakerTopCountries = cloakerStats ? Object.entries(cloakerStats.countries || {}).sort((a, b) => b[1] - a[1]).slice(0, 8) : [];
  const cloakerBotReasons = cloakerStats ? Object.entries(cloakerStats.bot_reasons || {}).sort((a, b) => b[1] - a[1]) : [];

  const navItems = [
    { id: 'overview', icon: BarChart3, label: 'Genel Bakış' },
    { id: 'users', icon: Users, label: 'Kullanıcılar' },
    { id: 'links', icon: Link2, label: 'Tüm Linkler' },
    { id: 'cloaker', icon: Layers, label: 'Cloaker' },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex">
      <aside className="w-64 bg-[#121214] border-r border-white/5 hidden lg:block">
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="BestURL" className="h-9 w-auto" />
            </Link>
          </div>
          <nav className="flex-1 p-4 space-y-2">
            <Link to="/panel" className="sidebar-nav-item">
              <Home className="w-5 h-5" /><span>Panel</span>
            </Link>
            {navItems.map(item => (
              <button key={item.id} onClick={() => setActiveTab(item.id)} className={`sidebar-nav-item w-full text-left ${activeTab === item.id ? 'active' : ''}`}>
                <item.icon className="w-5 h-5" /><span>{item.label}</span>
              </button>
            ))}
          </nav>
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{user?.username || 'Admin'}</p>
                <p className="text-xs text-amber-400">Admin</p>
              </div>
            </div>
            <Button variant="ghost" className="w-full justify-start text-slate-400 hover:text-white hover:bg-white/5" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />Çıkış Yap
            </Button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#0A0A0B]/80 backdrop-blur-xl border-b border-white/5">
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
            <div className="flex items-center gap-4">
              <Link to="/panel" className="lg:hidden p-2 text-slate-400 hover:text-white">
                <Home className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-400" />
                <h1 className="text-xl font-semibold text-white">Admin Paneli</h1>
              </div>
            </div>
            <div className="flex gap-1.5 lg:hidden overflow-x-auto">
              {navItems.map(item => (
                <button key={item.id} onClick={() => setActiveTab(item.id)} className={`px-2.5 py-1.5 rounded-lg text-xs whitespace-nowrap ${activeTab === item.id ? 'bg-white/10 text-white' : 'text-slate-400'}`}>
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:p-8">

          {activeTab === 'overview' && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                {[
                  { label: 'Toplam Kullanıcı', value: stats?.total_users || 0, icon: Users, color: 'cyan' },
                  { label: 'Toplam Link', value: stats?.total_links || 0, icon: Link2, color: 'emerald' },
                  { label: 'Toplam Tıklama', value: stats?.total_clicks || 0, icon: MousePointerClick, color: 'purple' },
                  { label: 'Bugün Tıklama', value: stats?.today_clicks || 0, icon: TrendingUp, color: 'amber' },
                  { label: 'Bugün Link', value: stats?.today_links || 0, icon: Calendar, color: 'pink' },
                ].map(({ label, value, icon: Icon, color }) => (
                  <div key={label} className="stat-card">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-xl bg-${color}-500/10 flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 text-${color}-400`} />
                      </div>
                    </div>
                    <p className="text-sm text-slate-500 mb-1">{label}</p>
                    <p className="text-2xl font-bold text-white">{value}</p>
                  </div>
                ))}
              </div>
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-[#121214] rounded-2xl border border-white/5 overflow-hidden">
                  <div className="p-4 sm:p-6 border-b border-white/5 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-white">Son Kullanıcılar</h2>
                    <button onClick={() => setActiveTab('users')} className="text-sm text-cyan-400 hover:text-cyan-300">Tümü</button>
                  </div>
                  <div className="divide-y divide-white/5">
                    {users.slice(0, 5).map((u) => (
                      <div key={u.id} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${u.is_admin ? 'bg-amber-500/20' : u.is_premium ? 'bg-purple-500/20' : 'bg-cyan-500/20'}`}>
                            <span className={`font-semibold ${u.is_admin ? 'text-amber-400' : u.is_premium ? 'text-purple-400' : 'text-cyan-400'}`}>{u.username?.charAt(0).toUpperCase() || 'U'}</span>
                          </div>
                          <div>
                            <p className="text-white font-medium">{u.username}</p>
                            <p className="text-sm text-slate-500">{u.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-cyan-400 font-medium">{u.link_count || 0}</span>
                          <p className="text-xs text-slate-500">link</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-[#121214] rounded-2xl border border-white/5 overflow-hidden">
                  <div className="p-4 sm:p-6 border-b border-white/5 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-white">Son Linkler</h2>
                    <button onClick={() => setActiveTab('links')} className="text-sm text-cyan-400 hover:text-cyan-300">Tümü</button>
                  </div>
                  <div className="divide-y divide-white/5">
                    {allLinks.slice(0, 5).map((l) => (
                      <div key={l.id} className="p-4 flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-white font-medium truncate">{l.title || l.short_code}</p>
                          <code className="text-cyan-400 text-xs">{DOMAIN}/{l.short_code}</code>
                          <p className="text-xs text-slate-500 mt-0.5">@{l.username}</p>
                        </div>
                        <div className="text-right ml-4">
                          <span className="text-white font-medium">{l.click_count || 0}</span>
                          <p className="text-xs text-slate-500">tıklama</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'users' && (
            <div className="bg-[#121214] rounded-2xl border border-white/5 overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-white/5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <h2 className="text-lg font-semibold text-white">Kullanıcı Yönetimi</h2>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input placeholder="Kullanıcı ara..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 w-full sm:w-64 bg-[#1C1C1E] border-white/10 text-white" />
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr className="bg-[#1C1C1E]">
                      <th>Kullanıcı</th><th>E-posta</th><th>Linkler</th><th>Tıklamalar</th><th>Durum</th><th>Tip</th><th>Kayıt</th><th>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.id}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${u.is_admin ? 'bg-amber-500/20' : u.is_premium ? 'bg-purple-500/20' : 'bg-cyan-500/20'}`}>
                              <span className={`text-sm font-semibold ${u.is_admin ? 'text-amber-400' : u.is_premium ? 'text-purple-400' : 'text-cyan-400'}`}>{u.username?.charAt(0).toUpperCase() || 'U'}</span>
                            </div>
                            <p className="text-white font-medium">{u.username}</p>
                          </div>
                        </td>
                        <td className="text-slate-300">{u.email}</td>
                        <td className="text-cyan-400 font-medium">{u.link_count || 0}</td>
                        <td className="text-purple-400 font-medium">{u.click_count || 0}</td>
                        <td><span className={`badge ${u.is_active !== false ? 'badge-success' : 'badge-error'}`}>{u.is_active !== false ? 'Aktif' : 'Pasif'}</span></td>
                        <td>
                          <div className="flex gap-1">
                            {u.is_admin && <span className="text-xs bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">Admin</span>}
                            {u.is_premium && <span className="text-xs bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">Premium</span>}
                            {!u.is_admin && !u.is_premium && <span className="text-xs bg-slate-500/20 text-slate-400 px-1.5 py-0.5 rounded">Ücretsiz</span>}
                          </div>
                        </td>
                        <td className="text-slate-400">{u.created_at ? new Date(u.created_at).toLocaleDateString('tr-TR') : '-'}</td>
                        <td>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleTogglePremium(u.id)} className="text-purple-400 hover:text-purple-300" title={u.is_premium ? 'Premium kaldır' : 'Premium yap'}><Crown className="w-4 h-4" /></Button>
                            {u.id !== user?.id && (
                              <>
                                <Button variant="ghost" size="icon" onClick={() => handleToggleAdmin(u.id)} className="text-amber-400 hover:text-amber-300" title={u.is_admin ? 'Admin kaldır' : 'Admin yap'}>{u.is_admin ? <ShieldOff className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}</Button>
                                <Button variant="ghost" size="icon" onClick={() => handleToggleUserStatus(u.id)} className={u.is_active !== false ? 'text-amber-400 hover:text-amber-300' : 'text-emerald-400 hover:text-emerald-300'} title={u.is_active !== false ? 'Devre dışı bırak' : 'Aktif et'}>{u.is_active !== false ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}</Button>
                                {!u.is_admin && <Button variant="ghost" size="icon" onClick={() => handleDeleteUser(u.id, u.username)} className="text-red-400 hover:text-red-300" title="Sil"><Trash2 className="w-4 h-4" /></Button>}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredUsers.length === 0 && <div className="p-12 text-center text-slate-500">{searchQuery ? 'Sonuç bulunamadı' : 'Henüz kullanıcı yok'}</div>}
            </div>
          )}

          {activeTab === 'links' && (
            <div className="bg-[#121214] rounded-2xl border border-white/5 overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-white/5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <h2 className="text-lg font-semibold text-white">Tüm Linkler ({allLinks.length})</h2>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input placeholder="Link ara..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 w-full sm:w-64 bg-[#1C1C1E] border-white/10 text-white" />
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr className="bg-[#1C1C1E]">
                      <th>Link</th><th>Hedef URL</th><th>Kullanıcı</th><th>Tıklama</th><th>Durum</th><th>Tarih</th><th>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLinks.map((l) => (
                      <tr key={l.id}>
                        <td>
                          <p className="text-white font-medium">{l.title || l.short_code}</p>
                          <code className="text-cyan-400 text-xs">{DOMAIN}/{l.short_code}</code>
                          {l.campaign_tag && <span className="text-xs bg-cyan-500/10 text-cyan-400 px-1.5 py-0.5 rounded ml-2">{l.campaign_tag}</span>}
                        </td>
                        <td className="max-w-[200px]"><p className="text-slate-300 text-sm truncate" title={l.original_url}>{l.original_url}</p></td>
                        <td className="text-slate-300">@{l.username}</td>
                        <td className="text-purple-400 font-medium">{l.click_count || 0}</td>
                        <td><span className={`badge ${l.is_active ? 'badge-success' : 'badge-error'}`}>{l.is_active ? 'Aktif' : 'Pasif'}</span></td>
                        <td className="text-slate-400 text-sm">{l.created_at ? new Date(l.created_at).toLocaleDateString('tr-TR') : '-'}</td>
                        <td>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleViewAnalytics(l)} className="text-cyan-400 hover:text-cyan-300" title="Analitik"><BarChart2 className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(l)} className="text-blue-400 hover:text-blue-300" title="Düzenle"><Pencil className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => window.open(`https://${DOMAIN}/${l.short_code}`, '_blank')} className="text-slate-400 hover:text-white" title="Aç"><ExternalLink className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => handleToggleLinkStatus(l.id)} className={l.is_active ? 'text-amber-400 hover:text-amber-300' : 'text-emerald-400 hover:text-emerald-300'} title={l.is_active ? 'Devre dışı bırak' : 'Aktif et'}>{l.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}</Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteLink(l.id)} className="text-red-400 hover:text-red-300" title="Sil"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredLinks.length === 0 && <div className="p-12 text-center text-slate-500">{searchQuery ? 'Sonuç bulunamadı' : 'Henüz link yok'}</div>}
            </div>
          )}

          {activeTab === 'cloaker' && (
            <div className="space-y-6">
              <div className="bg-[#121214] border border-cyan-500/20 rounded-2xl p-4 flex items-start gap-3">
                <Layers className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-medium text-sm">Cloaker Sistemi</p>
                  <p className="text-slate-400 text-sm mt-0.5">Gerçek insanlar <span className="text-emerald-400">real_url</span>'ye, botlar &amp; tarayıcılar <span className="text-red-400">safe_url</span>'ye yönlendirilir. Link formatı: <code className="text-cyan-400">{window.location.origin}/c/KOD</code></p>
                  <p className="text-slate-500 text-xs mt-1">Bot tespiti: UA pattern (80+) → Header fingerprint (Accept-Language/Accept) → IP hosting/proxy kontrolü (ip-api.com, cached)</p>
                </div>
              </div>

              <div className="bg-[#121214] rounded-2xl border border-white/5 overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-white/5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Layers className="w-5 h-5 text-cyan-400" />
                      Cloaker Linkleri ({cloakedLinks.length})
                    </h2>
                    <div className="flex gap-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input placeholder="Ara..." value={cloakerSearch} onChange={(e) => setCloakerSearch(e.target.value)} className="pl-10 w-full sm:w-52 bg-[#1C1C1E] border-white/10 text-white" />
                      </div>
                      <Button onClick={openCloakerCreate} className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold shrink-0">
                        <Plus className="w-4 h-4 mr-1.5" />Yeni
                      </Button>
                    </div>
                  </div>
                </div>

                {cloakerLoading ? (
                  <div className="flex items-center justify-center py-16"><div className="spinner" /></div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr className="bg-[#1C1C1E]">
                          <th>Link</th>
                          <th>İnsan URL (Real)</th>
                          <th>Bot URL (Safe)</th>
                          <th><span className="flex items-center gap-1"><User className="w-3.5 h-3.5 text-emerald-400" />İnsan</span></th>
                          <th><span className="flex items-center gap-1"><Bot className="w-3.5 h-3.5 text-red-400" />Bot</span></th>
                          <th>Bot %</th>
                          <th>Durum</th>
                          <th>İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCloaked.map((l) => {
                          const total = (l.human_count || 0) + (l.bot_count || 0);
                          const botPct = total > 0 ? Math.round((l.bot_count / total) * 100) : 0;
                          return (
                            <tr key={l.id}>
                              <td>
                                <p className="text-white font-medium">{l.title || l.short_code}</p>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <code className="text-cyan-400 text-xs">/c/{l.short_code}</code>
                                  <button onClick={() => copyLink(l.short_code)} className="text-slate-500 hover:text-slate-300 transition-colors">
                                    <Copy className="w-3 h-3" />
                                  </button>
                                </div>
                              </td>
                              <td className="max-w-[160px]"><p className="text-emerald-400 text-xs truncate" title={l.real_url}>{l.real_url}</p></td>
                              <td className="max-w-[160px]"><p className="text-red-400 text-xs truncate" title={l.safe_url}>{l.safe_url}</p></td>
                              <td className="text-emerald-400 font-medium">{l.human_count || 0}</td>
                              <td className="text-red-400 font-medium">{l.bot_count || 0}</td>
                              <td>
                                <div className="flex items-center gap-2">
                                  <div className="w-16 bg-white/5 rounded-full h-1.5">
                                    <div className="h-1.5 rounded-full bg-red-400 transition-all" style={{ width: `${botPct}%` }} />
                                  </div>
                                  <span className="text-slate-400 text-xs">{botPct}%</span>
                                </div>
                              </td>
                              <td><span className={`badge ${l.is_active ? 'badge-success' : 'badge-error'}`}>{l.is_active ? 'Aktif' : 'Pasif'}</span></td>
                              <td>
                                <div className="flex items-center gap-1">
                                  <Button variant="ghost" size="icon" onClick={() => handleViewCloakerStats(l)} className="text-cyan-400 hover:text-cyan-300" title="İstatistikler"><BarChart2 className="w-4 h-4" /></Button>
                                  <Button variant="ghost" size="icon" onClick={() => openCloakerEdit(l)} className="text-blue-400 hover:text-blue-300" title="Düzenle"><Pencil className="w-4 h-4" /></Button>
                                  <Button variant="ghost" size="icon" onClick={() => handleToggleCloaker(l.id, l.is_active)} className={l.is_active ? 'text-amber-400 hover:text-amber-300' : 'text-emerald-400 hover:text-emerald-300'} title={l.is_active ? 'Devre dışı bırak' : 'Aktif et'}>{l.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</Button>
                                  <Button variant="ghost" size="icon" onClick={() => handleDeleteCloaker(l.id)} className="text-red-400 hover:text-red-300" title="Sil"><Trash2 className="w-4 h-4" /></Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {!cloakerLoading && filteredCloaked.length === 0 && (
                  <div className="p-12 text-center text-slate-500">
                    {cloakerSearch ? 'Sonuç bulunamadı' : (
                      <div>
                        <Layers className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                        <p>Henüz cloaker linki yok</p>
                        <button onClick={openCloakerCreate} className="text-cyan-400 hover:text-cyan-300 text-sm mt-2">İlk linki oluştur →</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </main>

      {editingLink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-[#121214] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-white/5">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2"><Pencil className="w-5 h-5 text-blue-400" />Link Düzenle</h3>
              <button onClick={() => setEditingLink(null)} className="text-slate-400 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-xs text-slate-500 mb-1">Kullanıcı</p>
                <p className="text-slate-300 text-sm">@{editingLink.username}</p>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Başlık</label>
                <Input value={editForm.title} onChange={(e) => setEditForm(f => ({ ...f, title: e.target.value }))} placeholder="Link başlığı" className="bg-[#1C1C1E] border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Hedef URL</label>
                <Input value={editForm.original_url} onChange={(e) => setEditForm(f => ({ ...f, original_url: e.target.value }))} placeholder="https://example.com" className="bg-[#1C1C1E] border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Kısa Kod</label>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 text-sm shrink-0">{DOMAIN}/</span>
                  <Input value={editForm.short_code} onChange={(e) => setEditForm(f => ({ ...f, short_code: e.target.value }))} placeholder="kisa-kod" className="bg-[#1C1C1E] border-white/10 text-white" />
                </div>
              </div>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <Button variant="ghost" className="flex-1 border border-white/10 text-slate-400 hover:text-white" onClick={() => setEditingLink(null)}>İptal</Button>
              <Button className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold" onClick={handleSaveEdit} disabled={editSaving}>
                {editSaving ? <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />Kaydediliyor...</span> : <span className="flex items-center gap-2"><Save className="w-4 h-4" />Kaydet</span>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {analyticsLink && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-[#121214] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-[#121214] z-10">
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2"><BarChart2 className="w-5 h-5 text-cyan-400 shrink-0" /><span className="truncate">{analyticsLink.title || analyticsLink.short_code}</span></h3>
                <p className="text-xs text-slate-500 mt-0.5">@{analyticsLink.username} · <code className="text-cyan-400">{DOMAIN}/{analyticsLink.short_code}</code></p>
              </div>
              <button onClick={() => { setAnalyticsLink(null); setAnalyticsData(null); }} className="text-slate-400 hover:text-white ml-4 shrink-0"><X className="w-5 h-5" /></button>
            </div>
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-24"><div className="spinner" /></div>
            ) : analyticsData ? (
              <div className="p-5 space-y-6">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-[#1C1C1E] rounded-xl p-4 text-center"><p className="text-2xl font-bold text-white">{analyticsData.total_clicks}</p><p className="text-xs text-slate-500 mt-1">İnsan Tıklaması</p></div>
                  <div className="bg-[#1C1C1E] rounded-xl p-4 text-center"><p className="text-2xl font-bold text-slate-400">{analyticsData.bot_clicks_filtered}</p><p className="text-xs text-slate-500 mt-1">Bot Filtrelendi</p></div>
                  <div className="bg-[#1C1C1E] rounded-xl p-4 text-center"><p className="text-2xl font-bold text-cyan-400">{analyticsLink.click_count}</p><p className="text-xs text-slate-500 mt-1">Toplam Sayaç</p></div>
                </div>
                {recentDailyClicks.length > 0 && (
                  <div className="bg-[#1C1C1E] rounded-xl p-4">
                    <p className="text-sm font-medium text-white mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-cyan-400" />Son 14 Gün</p>
                    <ResponsiveContainer width="100%" height={140}>
                      <BarChart data={recentDailyClicks} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                        <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                        <YAxis tick={{ fill: '#64748b', fontSize: 10 }} allowDecimals={false} />
                        <Tooltip contentStyle={{ background: '#1C1C1E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }} labelStyle={{ color: '#94a3b8', fontSize: 11 }} itemStyle={{ color: '#06B6D4' }} />
                        <Bar dataKey="clicks" fill="#06B6D4" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-[#1C1C1E] rounded-xl p-4">
                    <p className="text-sm font-medium text-white mb-3 flex items-center gap-2"><Monitor className="w-4 h-4 text-purple-400" />Cihaz</p>
                    {Object.entries(analyticsData.devices || {}).sort((a, b) => b[1] - a[1]).map(([d, c], i) => <StatBar key={d} label={d} value={c} total={deviceTotal} color={SOURCE_COLORS[i]} />)}
                    {Object.keys(analyticsData.devices || {}).length === 0 && <p className="text-slate-500 text-sm">Veri yok</p>}
                  </div>
                  <div className="bg-[#1C1C1E] rounded-xl p-4">
                    <p className="text-sm font-medium text-white mb-3 flex items-center gap-2"><Smartphone className="w-4 h-4 text-emerald-400" />Tarayıcı</p>
                    {topBrowsers.map(([b, c], i) => <StatBar key={b} label={b} value={c} total={browserTotal} color={SOURCE_COLORS[i]} />)}
                    {topBrowsers.length === 0 && <p className="text-slate-500 text-sm">Veri yok</p>}
                  </div>
                </div>
                {topCountries.length > 0 && (
                  <div className="bg-[#1C1C1E] rounded-xl p-4">
                    <p className="text-sm font-medium text-white mb-3 flex items-center gap-2"><Globe className="w-4 h-4 text-amber-400" />Ülke</p>
                    <div className="grid sm:grid-cols-2 gap-x-6">
                      {topCountries.map(([country, count], i) => <StatBar key={country} label={country} value={count} total={countryTotal} color={SOURCE_COLORS[i % SOURCE_COLORS.length]} />)}
                    </div>
                  </div>
                )}
                {Object.keys(analyticsData.source_totals || {}).length > 0 && (
                  <div className="bg-[#1C1C1E] rounded-xl p-4">
                    <p className="text-sm font-medium text-white mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-pink-400" />Trafik Kaynağı</p>
                    <div className="grid sm:grid-cols-2 gap-x-6">
                      {Object.entries(analyticsData.source_totals).sort((a, b) => b[1] - a[1]).map(([src, count], i) => <StatBar key={src} label={src} value={count} total={analyticsData.total_clicks} color={SOURCE_COLORS[i % SOURCE_COLORS.length]} />)}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {showCloakerCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-[#121214] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-white/5">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2"><Layers className="w-5 h-5 text-cyan-400" />{editingCloaker ? 'Cloaker Linki Düzenle' : 'Yeni Cloaker Linki'}</h3>
              <button onClick={() => setShowCloakerCreate(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {!editingCloaker && (
                <div>
                  <label className="block text-sm text-slate-400 mb-1.5">Kısa Kod <span className="text-red-400">*</span></label>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 text-sm shrink-0">{window.location.origin}/c/</span>
                    <Input value={cloakerForm.short_code} onChange={(e) => setCloakerForm(f => ({ ...f, short_code: e.target.value }))} placeholder="kampanya1" className="bg-[#1C1C1E] border-white/10 text-white" />
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Başlık (opsiyonel)</label>
                <Input value={cloakerForm.title} onChange={(e) => setCloakerForm(f => ({ ...f, title: e.target.value }))} placeholder="Kampanya adı" className="bg-[#1C1C1E] border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">
                  <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-emerald-400" />İnsan URL (Real URL) <span className="text-red-400">*</span></span>
                </label>
                <Input value={cloakerForm.real_url} onChange={(e) => setCloakerForm(f => ({ ...f, real_url: e.target.value }))} placeholder="https://gercek-hedef.com/teklif" className="bg-[#1C1C1E] border-white/10 text-white" />
                <p className="text-xs text-slate-500 mt-1">Gerçek ziyaretçiler buraya yönlendirilir</p>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">
                  <span className="flex items-center gap-1.5"><Bot className="w-3.5 h-3.5 text-red-400" />Bot URL (Safe URL) <span className="text-red-400">*</span></span>
                </label>
                <Input value={cloakerForm.safe_url} onChange={(e) => setCloakerForm(f => ({ ...f, safe_url: e.target.value }))} placeholder="https://guvenli-sayfa.com" className="bg-[#1C1C1E] border-white/10 text-white" />
                <p className="text-xs text-slate-500 mt-1">Botlar ve tarayıcılar buraya yönlendirilir</p>
              </div>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <Button variant="ghost" className="flex-1 border border-white/10 text-slate-400 hover:text-white" onClick={() => setShowCloakerCreate(false)}>İptal</Button>
              <Button className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold" onClick={handleSaveCloaker} disabled={cloakerSaving}>
                {cloakerSaving ? <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />Kaydediliyor...</span> : <span className="flex items-center gap-2"><Save className="w-4 h-4" />{editingCloaker ? 'Güncelle' : 'Oluştur'}</span>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {cloakerStatsLink && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-[#121214] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-[#121214] z-10">
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2"><Layers className="w-5 h-5 text-cyan-400 shrink-0" /><span className="truncate">{cloakerStatsLink.title || cloakerStatsLink.short_code} — Cloaker İstatistikleri</span></h3>
                <p className="text-xs text-slate-500 mt-0.5"><code className="text-cyan-400">{window.location.origin}/c/{cloakerStatsLink.short_code}</code></p>
              </div>
              <button onClick={() => { setCloakerStatsLink(null); setCloakerStats(null); }} className="text-slate-400 hover:text-white ml-4 shrink-0"><X className="w-5 h-5" /></button>
            </div>
            {cloakerStatsLoading ? (
              <div className="flex items-center justify-center py-24"><div className="spinner" /></div>
            ) : cloakerStats ? (
              <div className="p-5 space-y-6">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-[#1C1C1E] rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-400">{cloakerStats.human_count}</p>
                    <p className="text-xs text-slate-500 mt-1 flex items-center justify-center gap-1"><User className="w-3 h-3" />İnsan</p>
                  </div>
                  <div className="bg-[#1C1C1E] rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-red-400">{cloakerStats.bot_count}</p>
                    <p className="text-xs text-slate-500 mt-1 flex items-center justify-center gap-1"><Bot className="w-3 h-3" />Bot</p>
                  </div>
                  <div className="bg-[#1C1C1E] rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-white">{cloakerStats.total > 0 ? Math.round((cloakerStats.bot_count / cloakerStats.total) * 100) : 0}%</p>
                    <p className="text-xs text-slate-500 mt-1">Bot Oranı</p>
                  </div>
                </div>

                {cloakerDailyData.length > 0 && (
                  <div className="bg-[#1C1C1E] rounded-xl p-4">
                    <p className="text-sm font-medium text-white mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-cyan-400" />Son 14 Gün (İnsan vs Bot)</p>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={cloakerDailyData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                        <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                        <YAxis tick={{ fill: '#64748b', fontSize: 10 }} allowDecimals={false} />
                        <Tooltip contentStyle={{ background: '#1C1C1E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }} labelStyle={{ color: '#94a3b8', fontSize: 11 }} />
                        <Bar dataKey="human" name="İnsan" fill="#10B981" radius={[2, 2, 0, 0]} stackId="a" />
                        <Bar dataKey="bot" name="Bot" fill="#EF4444" radius={[2, 2, 0, 0]} stackId="a" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                  {cloakerBotReasons.length > 0 && (
                    <div className="bg-[#1C1C1E] rounded-xl p-4">
                      <p className="text-sm font-medium text-white mb-3 flex items-center gap-2"><Bot className="w-4 h-4 text-red-400" />Bot Tespit Nedeni</p>
                      {cloakerBotReasons.map(([reason, count], i) => (
                        <StatBar key={reason} label={reason} value={count} total={cloakerStats.bot_count} color={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                      ))}
                    </div>
                  )}
                  {cloakerTopCountries.length > 0 && (
                    <div className="bg-[#1C1C1E] rounded-xl p-4">
                      <p className="text-sm font-medium text-white mb-3 flex items-center gap-2"><Globe className="w-4 h-4 text-amber-400" />Ülke</p>
                      {cloakerTopCountries.map(([country, count], i) => (
                        <StatBar key={country} label={country} value={count} total={cloakerStats.total} color={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                      ))}
                    </div>
                  )}
                </div>

                {cloakerStats.recent_bot_uas?.length > 0 && (
                  <div className="bg-[#1C1C1E] rounded-xl p-4">
                    <p className="text-sm font-medium text-white mb-3 flex items-center gap-2"><Bot className="w-4 h-4 text-slate-400" />Tespit Edilen Bot UA'ları</p>
                    <div className="space-y-1">
                      {cloakerStats.recent_bot_uas.map((ua, i) => (
                        <p key={i} className="text-xs text-slate-500 font-mono bg-white/5 px-2 py-1 rounded truncate" title={ua}>{ua}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
